// Чистата логика на разбивката на продаденото при разнасяне: плоските редове
// от BE (продаваем × сервитьор × станция) → двата блока при избрания отчет в
// списъка с чакащите (`PendingReportBreakdown.tsx`):
//  • „Сервитьори": по сервитьор — име · N сметки · общо бр, под него рецепта × к-во;
//  • „Станции": по станция — име · „→ склад X" / „без склад" · общо бр, под нея
//    рецепта × к-во.
// Нищо от това не влияе на изписването — само информация. Сборът на всяка
// група и на всички групи е числото в „Продадено" (BE брои със същите филтри).
import type { ReportPostingRecipe } from "../../../../api/inventory";
import type {
  ReportSalesBreakdown,
  ReportSalesWaiter,
} from "../../../../api/inventoryReportBreakdown";

// Блокът има смисъл при поне един сервитьор или продаден ред (сервитьор без
// продажби — почистени сметки — също се показва)
export const hasSalesBreakdown = (breakdown: ReportSalesBreakdown | null | undefined) =>
  !!breakdown && (breakdown.rows.length > 0 || breakdown.waiters.length > 0);

// Името на сервитьора за показване: снимката от личния отчет, иначе „#id"
export const waiterLabel = (waiter: Pick<ReportSalesWaiter, "waiterId" | "name">) =>
  waiter.name?.trim() || (waiter.waiterId != null ? `#${waiter.waiterId}` : "—");

const byNameThenId = <T extends { name: string; sellableId: number }>(left: T, right: T) =>
  left.name.localeCompare(right.name, "bg") || left.sellableId - right.sellableId;

// ─── „Сервитьори" / „Станции": групи ─────────────────────────────────────
export type SoldGroupLine = {
  sellableId: number;
  name: string;
  quantity: number;
  isProduct: boolean;
};

export type SoldGroup = {
  key: string;
  title: string;
  // складът на станцията (подсказка „→ склад X"); `null` — станция без склад;
  // `undefined` — групата не е станция
  warehouseName?: string | null;
  // сметките на сервитьора в отчета; `undefined` — групата не е сервитьор
  billsCount?: number;
  lines: SoldGroupLine[];
  totalQuantity: number;
};

const recipeLookup = (recipes: ReportPostingRecipe[]) => {
  const byId = new Map(recipes.map((recipe) => [recipe.recipeId, recipe]));
  return (sellableId: number) => byId.get(sellableId);
};

// Редовете на една група: един ред на продаваем (сбор от станциите или от
// сервитьорите), по име като списъка „Продадено"; неизвестен в прегледа — „#id"
const groupLines = (
  quantities: Map<number, number>,
  recipeOf: (sellableId: number) => ReportPostingRecipe | undefined,
): SoldGroupLine[] =>
  [...quantities]
    .filter(([, quantity]) => quantity !== 0)
    .map(([sellableId, quantity]) => {
      const recipe = recipeOf(sellableId);
      return {
        sellableId,
        name: recipe?.name ?? `#${sellableId}`,
        quantity,
        isProduct: recipe?.isProduct ?? false,
      };
    })
    .sort(byNameThenId);

const sumQuantities = (lines: SoldGroupLine[]) => lines.reduce((sum, line) => sum + line.quantity, 0);

const quantitiesBy = <K>(breakdown: ReportSalesBreakdown, keyOf: (row: ReportSalesBreakdown["rows"][number]) => K) => {
  const groups = new Map<K, Map<number, number>>();
  for (const row of breakdown.rows) {
    const key = keyOf(row);
    const bySellable = groups.get(key) ?? new Map<number, number>();
    bySellable.set(row.sellableId, (bySellable.get(row.sellableId) ?? 0) + row.quantity);
    groups.set(key, bySellable);
  }
  return groups;
};

// По сервитьори — в реда от BE (по име); сервитьор без продажби остава с
// празна група (сметките му са почистени), за да се види, че е бил в отчета.
// Сервитьор, когото го няма в списъка, е без брой сметки.
export const groupSoldByWaiter = (
  breakdown: ReportSalesBreakdown,
  recipes: ReportPostingRecipe[],
): SoldGroup[] => {
  const recipeOf = recipeLookup(recipes);
  const quantities = quantitiesBy(breakdown, (row) => row.waiterId);
  const groups: SoldGroup[] = breakdown.waiters.map((waiter) => {
    const lines = groupLines(quantities.get(waiter.waiterId) ?? new Map(), recipeOf);
    return {
      key: `waiter-${waiter.waiterId ?? "none"}`,
      title: waiterLabel(waiter),
      billsCount: waiter.billsCount,
      lines,
      totalQuantity: sumQuantities(lines),
    };
  });
  // Ред на сервитьор, когото няма в списъка (не би трябвало) — пак се показва
  const known = new Set(breakdown.waiters.map((waiter) => waiter.waiterId));
  for (const [waiterId, bySellable] of quantities) {
    if (known.has(waiterId)) continue;
    const lines = groupLines(bySellable, recipeOf);
    groups.push({
      key: `waiter-${waiterId ?? "none"}`,
      title: waiterLabel({ waiterId, name: null }),
      lines,
      totalQuantity: sumQuantities(lines),
    });
  }
  return groups;
};

// По станции — в реда на org екрана (от BE), с подсказката за склада
export const groupSoldByStation = (
  breakdown: ReportSalesBreakdown,
  recipes: ReportPostingRecipe[],
): SoldGroup[] => {
  const recipeOf = recipeLookup(recipes);
  const quantities = quantitiesBy(breakdown, (row) => row.stationId);
  const stationIds = [
    ...breakdown.stations.map((station) => station.stationId),
    ...[...quantities.keys()].filter(
      (stationId) => !breakdown.stations.some((station) => station.stationId === stationId),
    ),
  ];
  return stationIds
    .filter((stationId) => quantities.has(stationId))
    .map((stationId) => {
      const station = breakdown.stations.find((candidate) => candidate.stationId === stationId);
      const lines = groupLines(quantities.get(stationId) ?? new Map(), recipeOf);
      return {
        key: `station-${stationId}`,
        title: station?.name ?? `#${stationId}`,
        warehouseName: station?.warehouseName ?? null,
        lines,
        totalQuantity: sumQuantities(lines),
      };
    });
};

// Имената на сервитьорите за подсказката в заглавието на разнасянето
export const waiterNamesText = (breakdown: ReportSalesBreakdown) =>
  breakdown.waiters.map(waiterLabel).join(", ");
